import { useEffect, useState, useCallback, useMemo } from "react";
import { fetchTradespersonFeeBreakdown } from "../service";
import DivSpinner from "../../../components/divSpinner";
import ModalTemplate from "../../../components/modal/modalTemplate";
import { breakTextToHtml, formatMoney } from "../../../utils";

const TradespersonFeeBreakdown = ({ job_id, show, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [breakdown, setBreakdown] = useState(null);

    const fetchBreakdown = useCallback(async () => {
        if (!job_id) return;

        try {
            setLoading(true);
            const response = await fetchTradespersonFeeBreakdown(job_id);
            if (response?.status === "Success") {
                setBreakdown(response.data);
            }
        } catch (error) {
            console.error("Tradesperson fee breakdown error:", error);
        } finally {
            setLoading(false);
        }
    }, [job_id]);

    useEffect(() => {
        if (show) fetchBreakdown();
    }, [show, fetchBreakdown]);

    const LoadingComponent = useMemo(
        () => (
            <div style={{ minHeight: "250px" }} className="d-flex align-items-center justify-content-center">
                <DivSpinner />
            </div>
        ),
        []
    );

    const {
        ftm_actual_working_time = 0,
        ftm_working_time,
        ftm_working_time_fee,
        ftm_working_time_company_commission,
        ftm_working_time_total,
        total_materials_fee,
        materials_company_commission,
        total_materials_fee_with_commission,
        total_cost,
        ftm_job_discount,
    } = breakdown || {};

    const renderListItem = (label, value) => (
        <li className="list-group-item border-0 d-flex justify-content-between ps-0 border-radius-lg">
            <h6 className="text-dark mb-1 font-weight-bold text-sm">{label}</h6>
            <div className="text-sm">{value}</div>
        </li>
    );

    return (
        <ModalTemplate
            show={show}
            closeFun={onClose}
            centered
            title="Tradesperson Fee Breakdown"
            size={'lg'}
            body={
                loading ? (
                    LoadingComponent
                ) : (
                    <>
                        {/* Hourly Rate Calculation */}
                        <div className="card mb-4">
                            <div className="card-body pt-4 p-3">
                                <h6 className="text-uppercase text-body text-xs font-weight-bolder mb-3">Hourly rate calculation</h6>
                                <div className="table-responsive">
                                    <table className="table align-items-center">
                                        <tbody>
                                            {breakdown?.hourly_rate_breakdown?.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="w-40 ps-1">
                                                         <div className="d-flex flex-column">
                                                            <h6 className="mb-1 text-dark text-sm">{item.title}</h6>
                                                            <span className="text-xs" dangerouslySetInnerHTML={{ __html: breakTextToHtml(item.description, 50) }}/>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <h6 className="text-sm font-weight-normal mb-0">{item.percentage}%</h6>
                                                    </td>
                                                    <td>
                                                        <h6 className="text-sm font-weight-normal mb-0">{formatMoney(item.current_rate)}</h6>
                                                    </td>
                                                    <td className="align-middle text-sm">
                                                        <h6 className="text-sm font-weight-normal mb-0">{formatMoney(item.new_rate)}</h6>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Full Breakdown */}
                        <div className="card">
                            <div className="card-body">
                                <h6 className="text-uppercase text-body text-xs font-weight-bolder mb-3">
                                    Full Breakdown
                                </h6>

                                <ul className="list-group">
                                    {renderListItem("Actual Working Time", `${ftm_actual_working_time} mins`)}
                                    {renderListItem(
                                        "Billable Working Time",
                                        ftm_working_time?.text || "0.0 hrs"
                                    )}
                                    {renderListItem(
                                        "Billable Working Time Fee",
                                        formatMoney(ftm_working_time_fee)
                                    )}
                                    {renderListItem(
                                        "Working Time – Company Commission",
                                        formatMoney(ftm_working_time_company_commission)
                                    )}
                                    {renderListItem(
                                        "Total Working Time Charge",
                                        formatMoney(ftm_working_time_total)
                                    )}
                                    {renderListItem(
                                        "Materials Cost",
                                        formatMoney(total_materials_fee)
                                    )}
                                    {renderListItem(
                                        "Materials – Company Commission",
                                        formatMoney(materials_company_commission)
                                    )}
                                    {renderListItem(
                                        "Total Materials Charge (Incl. Commission)",
                                        formatMoney(total_materials_fee_with_commission)
                                    )}
                                    {renderListItem(
                                        `Discount (${ftm_job_discount?.type === 'Percentage' ? ftm_job_discount?.value+'%' : ftm_job_discount?.type})`,
                                        formatMoney(ftm_job_discount?.amount)
                                    )}
                                    {renderListItem(
                                        "Total Cost",
                                        formatMoney(total_cost)
                                    )}
                                </ul>
                            </div>
                        </div>
                    </>
                )
            }
            footer={null}
        />
    );
};

export default TradespersonFeeBreakdown;